import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { FileText, ClipboardList, Receipt, PlusCircle, Trash2, ArrowRight, TrendingUp } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const STAGES = [
    { key: "quotes", label: "1 · Quotations", icon: ClipboardList, hint: "Offer to client" },
    { key: "orders", label: "2 · Sales Orders", icon: FileText, hint: "Confirmed award" },
    { key: "invoice", label: "3 · Invoice", icon: Receipt, hint: "Via Quick Entry" },
];

export default function Sales() {
    const [tab, setTab] = useState("quotes");
    const [quotes, setQuotes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [showForm, setShowForm] = useState(null);

    const refresh = async () => {
        const [q, o, c, p] = await Promise.all([
            api.get("/sales/quotations"),
            api.get("/sales/orders"),
            api.get("/entry/clients"),
            api.get("/projects"),
        ]);
        setQuotes(q.data.quotations);
        setOrders(o.data.orders);
        setClients(c.data.clients);
        setProjects(p.data.projects);
    };
    useEffect(() => { refresh(); }, []);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// SALES · QUOTE-TO-CASH</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3"><TrendingUp className="text-[var(--brand-amber)]" size={26} /> Quotation → Sales Order → Invoice</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    Invoice posting via <Link to="/entry" className="text-[var(--brand-amber)] hover:underline">Quick Entry / SALES</Link> — same accounting engine
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="sc-quotes-open" label="Open Quotations" value={quotes.filter((q) => q.status === "Open").length} format="custom" accent />
                <KpiCard testId="sc-quotes-value" label="Pipeline Value" value={quotes.filter((q) => q.status === "Open").reduce((s, q) => s + (q.total_value || 0), 0)} sub={`${quotes.length} total`} />
                <KpiCard testId="sc-orders-open" label="Order Book (Open)" value={orders.filter((o) => o.status !== "Completed").reduce((s, o) => s + (o.total_value - (o.invoiced_amount || 0)), 0)} sub={`${orders.length} orders`} />
                <KpiCard testId="sc-conv-rate" label="Q→SO Conversion" value={quotes.length ? Math.round(100 * quotes.filter((q) => q.status === "Converted").length / quotes.length) + "%" : "—"} format="custom" />
            </div>

            <div className="grid grid-cols-3 gap-2">
                {STAGES.map((s, idx) => {
                    const Icon = s.icon;
                    return (
                        <button
                            key={s.key}
                            data-testid={`sc-tab-${s.key}`}
                            onClick={() => s.key !== "invoice" ? setTab(s.key) : window.location.assign("/entry")}
                            className={`border p-3 text-left transition-colors relative ${tab === s.key ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel hover:border-[var(--brand-amber)]"}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Icon size={14} className={tab === s.key ? "text-[var(--brand-amber)]" : "text-[var(--text-secondary)]"} />
                                <div className={`text-xs font-mono-fin uppercase tracking-[0.15em] ${tab === s.key ? "text-[var(--brand-amber)]" : "text-[var(--text-primary)]"}`}>{s.label}</div>
                            </div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider">{s.hint}</div>
                            {idx < STAGES.length - 1 && <ArrowRight size={12} className="absolute -right-3 top-1/2 -translate-y-1/2 text-[var(--brand-amber)] hidden md:block" />}
                        </button>
                    );
                })}
            </div>

            {tab === "quotes" && (
                <>
                    <div className="flex justify-end">
                        <button data-testid="sc-new-quote" onClick={() => setShowForm("quote")} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-1 hover:bg-[var(--bg-panel-hover)]">
                            <PlusCircle size={11} /> New Quotation
                        </button>
                    </div>
                    <Panel testId="sc-quotes-list" title="Quotations" subtitle={`${quotes.length} total`}>
                        {quotes.length === 0 ? <Empty text="No quotations yet." /> : <QuoteTable rows={quotes} onDelete={async (id) => { await api.delete(`/sales/quotations/${id}`); toast.success("Deleted"); refresh(); }} />}
                    </Panel>
                </>
            )}

            {tab === "orders" && (
                <>
                    <div className="flex justify-end">
                        <button data-testid="sc-new-order" onClick={() => setShowForm("order")} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-1 hover:bg-[var(--bg-panel-hover)]">
                            <PlusCircle size={11} /> New Sales Order
                        </button>
                    </div>
                    <Panel testId="sc-orders-list" title="Sales Orders" subtitle={`${orders.length} total`}>
                        {orders.length === 0 ? <Empty text="No sales orders yet." /> : <OrderTable rows={orders} onDelete={async (id) => { await api.delete(`/sales/orders/${id}`); toast.success("Deleted"); refresh(); }} />}
                    </Panel>
                </>
            )}

            {showForm === "quote" && <QuoteForm clients={clients} projects={projects} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); refresh(); }} />}
            {showForm === "order" && <OrderForm clients={clients} projects={projects} quotes={quotes.filter((q) => q.status === "Open")} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); refresh(); }} />}
        </div>
    );
}

const Empty = ({ text }) => <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">{text}</div>;

function QuoteTable({ rows, onDelete }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full font-mono-fin text-xs">
                <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                    <th className="text-left py-2">Quote No</th><th>Date</th><th>Client</th><th>Items</th><th className="text-right">Subtotal</th><th className="text-right">Total</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                    {rows.map((q) => (
                        <tr key={q.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                            <td className="py-2 text-[var(--brand-amber)]">{q.quote_no}</td>
                            <td className="text-[var(--text-secondary)]">{fmtDate(q.date)}</td>
                            <td className="text-[var(--text-primary)]">{q.client_name}</td>
                            <td className="text-[var(--text-secondary)]">{q.lines?.length || 0} lines</td>
                            <td className="text-right text-[var(--text-primary)]">{fmtINR(q.subtotal)}</td>
                            <td className="text-right text-amber">{fmtINR(q.total_value)}</td>
                            <td><span className="text-[10px] uppercase tracking-wider">{q.status}</span></td>
                            <td><button onClick={() => onDelete(q.id)} className="text-[var(--text-muted)] hover:text-neg"><Trash2 size={12} /></button></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function OrderTable({ rows, onDelete }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full font-mono-fin text-xs">
                <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                    <th className="text-left py-2">SO No</th><th>Date</th><th>Client</th><th>Project</th><th>Delivery</th><th className="text-right">Total</th><th className="text-right">Invoiced</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                    {rows.map((o) => (
                        <tr key={o.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                            <td className="py-2 text-[var(--brand-amber)]">{o.so_no}</td>
                            <td className="text-[var(--text-secondary)]">{fmtDate(o.date)}</td>
                            <td className="text-[var(--text-primary)]">{o.client_name}</td>
                            <td className="text-[var(--text-secondary)]">{o.project_code || "—"}</td>
                            <td className="text-[var(--text-secondary)]">{o.delivery_date ? fmtDate(o.delivery_date) : "—"}</td>
                            <td className="text-right text-amber">{fmtINR(o.total_value)}</td>
                            <td className="text-right text-pos">{fmtINR(o.invoiced_amount || 0)}</td>
                            <td><span className="text-[10px] uppercase tracking-wider">{o.status}</span></td>
                            <td><button onClick={() => onDelete(o.id)} className="text-[var(--text-muted)] hover:text-neg"><Trash2 size={12} /></button></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-panel border-b border-panel px-4 py-3 flex items-center justify-between">
                    <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">{title}</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">✕</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

const inputCls = "w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] outline-none";

function QuoteForm({ clients, projects, onClose, onSaved }) {
    const [form, setForm] = useState({ date: today(), client_id: "", project_id: "", valid_till: "", gst_rate: 18, remarks: "", lines: [{ item: "", unit: "Lot", qty: 1, rate: 0 }] });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const sub = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
    const gst = sub * (Number(form.gst_rate) / 100);
    const submit = async () => {
        try {
            const c = clients.find((x) => x.id === form.client_id);
            const p = projects.find((x) => x.id === form.project_id);
            const res = await api.post("/sales/quotations", {
                ...form, client_name: c?.name || "", project_code: p?.code || "", gst_rate: Number(form.gst_rate),
                lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), rate: Number(l.rate) })),
            });
            toast.success(`${res.data.quote_no} saved`); onSaved();
        } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    };
    return (
        <Modal title="New Quotation" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="sc-q-date" />
                <input type="date" value={form.valid_till} onChange={(e) => set("valid_till", e.target.value)} placeholder="Valid till" className={inputCls} />
                <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className={inputCls} data-testid="sc-q-client">
                    <option value="">-- Select Client --</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} className={inputCls}>
                    <option value="">-- Project (optional) --</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                </select>
                <select value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={inputCls}>
                    {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>GST @ {r}%</option>)}
                </select>
            </div>
            <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Line Items</div>
            <LineTable lines={form.lines} setLines={(fn) => setForm((f) => ({ ...f, lines: typeof fn === "function" ? fn(f.lines) : fn }))} />
            <div className="mt-3 border border-panel p-3 bg-[var(--bg-panel-hover)] grid grid-cols-3 gap-3 font-mono-fin text-xs">
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Subtotal</div><div className="text-[var(--text-primary)] mt-1">{fmtINR(sub)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">GST @ {form.gst_rate}%</div><div className="text-amber mt-1">{fmtINR(gst)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Total</div><div className="text-pos mt-1 font-semibold">{fmtINR(sub + gst)}</div></div>
            </div>
            <textarea placeholder="Remarks / T&C" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={`${inputCls} mt-3`} rows={2} />
            <div className="mt-4 flex justify-end">
                <button data-testid="sc-q-save" onClick={submit} className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold">Save Quotation</button>
            </div>
        </Modal>
    );
}

function OrderForm({ clients, projects, quotes, onClose, onSaved }) {
    const [form, setForm] = useState({ date: today(), quotation_id: "", client_id: "", project_id: "", delivery_date: "", gst_rate: 18, remarks: "", lines: [{ item: "", unit: "Lot", qty: 1, rate: 0 }] });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    useEffect(() => {
        if (form.quotation_id) {
            const q = quotes.find((x) => x.id === form.quotation_id);
            if (q) setForm((f) => ({ ...f, client_id: q.client_id, project_id: q.project_id || "", gst_rate: q.gst_rate, lines: q.lines.map((l) => ({ item: l.item, unit: l.unit, qty: l.qty, rate: l.rate })) }));
        }
    }, [form.quotation_id]);
    const sub = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
    const gst = sub * (Number(form.gst_rate) / 100);
    const submit = async () => {
        try {
            const c = clients.find((x) => x.id === form.client_id);
            const p = projects.find((x) => x.id === form.project_id);
            const res = await api.post("/sales/orders", {
                ...form, client_name: c?.name || "", project_code: p?.code || "", gst_rate: Number(form.gst_rate),
                lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), rate: Number(l.rate) })),
            });
            toast.success(`${res.data.so_no} raised`); onSaved();
        } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    };
    return (
        <Modal title="New Sales Order" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="sc-o-date" />
                <input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} placeholder="Delivery" className={inputCls} />
                <select value={form.quotation_id} onChange={(e) => set("quotation_id", e.target.value)} className={inputCls} data-testid="sc-o-quote">
                    <option value="">-- Link Quotation (optional) --</option>
                    {quotes.map((q) => <option key={q.id} value={q.id}>{q.quote_no} · {q.client_name}</option>)}
                </select>
                <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className={inputCls} data-testid="sc-o-client">
                    <option value="">-- Select Client --</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} className={inputCls}>
                    <option value="">-- Project (optional) --</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                </select>
            </div>
            <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Line Items</div>
            <LineTable lines={form.lines} setLines={(fn) => setForm((f) => ({ ...f, lines: typeof fn === "function" ? fn(f.lines) : fn }))} />
            <div className="mt-3 border border-panel p-3 bg-[var(--bg-panel-hover)] grid grid-cols-3 gap-3 font-mono-fin text-xs">
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Subtotal</div><div className="text-[var(--text-primary)] mt-1">{fmtINR(sub)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">GST @ {form.gst_rate}%</div><div className="text-amber mt-1">{fmtINR(gst)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Total</div><div className="text-pos mt-1 font-semibold">{fmtINR(sub + gst)}</div></div>
            </div>
            <textarea placeholder="Remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={`${inputCls} mt-3`} rows={2} />
            <div className="mt-4 flex justify-end">
                <button data-testid="sc-o-save" onClick={submit} className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold">Save Sales Order</button>
            </div>
        </Modal>
    );
}

function LineTable({ lines, setLines }) {
    return (
        <>
            <table className="w-full font-mono-fin text-xs mt-1">
                <thead className="border-b border-panel">
                    <tr className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                        <th className="text-left py-1">Item</th><th>Unit</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((l, i) => (
                        <tr key={i}>
                            <td className="py-1"><input value={l.item} onChange={(e) => setLines((ls) => { const n = [...ls]; n[i] = { ...n[i], item: e.target.value }; return n; })} className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input value={l.unit} onChange={(e) => setLines((ls) => { const n = [...ls]; n[i] = { ...n[i], unit: e.target.value }; return n; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input type="number" value={l.qty} onChange={(e) => setLines((ls) => { const n = [...ls]; n[i] = { ...n[i], qty: e.target.value }; return n; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td><input type="number" value={l.rate} onChange={(e) => setLines((ls) => { const n = [...ls]; n[i] = { ...n[i], rate: e.target.value }; return n; })} className="w-28 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td className="text-right text-amber">{fmtINR(Number(l.qty || 0) * Number(l.rate || 0))}</td>
                            <td>{lines.length > 1 && <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="text-neg"><Trash2 size={11} /></button>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button onClick={() => setLines((ls) => [...ls, { item: "", unit: "Lot", qty: 1, rate: 0 }])} className="mt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]"><PlusCircle size={11} className="inline" /> Add Line</button>
        </>
    );
}
